import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  CORNER_DISPLAY,
  CORNER_ORDER,
  CORNER_SHORT,
} from '../../mapGridGeometry';
import { isAllowedMosaicUrl } from '../../mapGridSession';

import style from './style.module.scss';

/** Map displayed mosaic pixels through object-fit: contain letterboxing. */
export const mosaicClickToPixel = (event, img, mosaicWidth, mosaicHeight) => {
  const rect = img.getBoundingClientRect();
  const srcW = mosaicWidth || img.naturalWidth;
  const srcH = mosaicHeight || img.naturalHeight;
  if (!rect.width || !rect.height || !srcW || !srcH) return null;

  const scale = Math.min(rect.width / srcW, rect.height / srcH);
  const drawW = srcW * scale;
  const drawH = srcH * scale;
  const left = rect.left + (rect.width - drawW) / 2;
  const top = rect.top + (rect.height - drawH) / 2;
  const x = event.clientX - left;
  const y = event.clientY - top;
  if (x < 0 || y < 0 || x > drawW || y > drawH) return null;

  return {
    mosaicX: (x / drawW) * srcW,
    mosaicY: (y / drawH) * srcH,
  };
};

export const containFrame = (wrap, img, mosaicWidth, mosaicHeight) => {
  if (!wrap || !img) return null;
  const wrapRect = wrap.getBoundingClientRect();
  const srcW = mosaicWidth || img.naturalWidth;
  const srcH = mosaicHeight || img.naturalHeight;
  if (!wrapRect.width || !wrapRect.height || !srcW || !srcH) return null;

  const scale = Math.min(wrapRect.width / srcW, wrapRect.height / srcH);
  const width = srcW * scale;
  const height = srcH * scale;
  return {
    left: (wrapRect.width - width) / 2,
    top: (wrapRect.height - height) / 2,
    width,
    height,
    srcW,
    srcH,
  };
};

const MIN_MOSAIC_SCALE = 1;
const MAX_MOSAIC_SCALE = 8;
const MOSAIC_DRAG_THRESHOLD = 4;

/** Left-pane mosaic viewer (used by Stream.js). */
const MapGridMosaic = ({
  mosaicUrl,
  mosaicWidth,
  mosaicHeight,
  corners,
  activeCorner,
  mappedRegions = [],
  onMosaicClick,
  onClose,
  disabled,
}) => {
  const safeMosaicUrl = isAllowedMosaicUrl(mosaicUrl) ? mosaicUrl : null;
  const imgRef = useRef(null);
  const wrapRef = useRef(null);
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);
  const [frame, setFrame] = useState(null);
  const [view, setView] = useState({ x: 0, y: 0, scale: 1 });
  const [dragging, setDragging] = useState(false);

  const updateFrame = useCallback(() => {
    setFrame(containFrame(wrapRef.current, imgRef.current, mosaicWidth, mosaicHeight));
  }, [mosaicWidth, mosaicHeight]);

  useEffect(() => {
    setView({ x: 0, y: 0, scale: 1 });
  }, [safeMosaicUrl]);

  useEffect(() => {
    updateFrame();
    const wrap = wrapRef.current;
    if (!wrap || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(updateFrame);
    observer.observe(wrap);
    return () => observer.disconnect();
  }, [updateFrame, safeMosaicUrl]);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return undefined;

    const handleWheel = (event) => {
      event.preventDefault();
      const rect = wrap.getBoundingClientRect();
      const mx = event.clientX - rect.left;
      const my = event.clientY - rect.top;
      const factor = event.deltaY > 0 ? 0.9 : 1.1;

      setView((current) => {
        const nextScale = Math.min(
          MAX_MOSAIC_SCALE,
          Math.max(MIN_MOSAIC_SCALE, current.scale * factor),
        );
        if (nextScale === current.scale) return current;
        if (nextScale === 1) return { x: 0, y: 0, scale: 1 };
        const ratio = nextScale / current.scale;
        return {
          scale: nextScale,
          x: mx - (mx - current.x) * ratio,
          y: my - (my - current.y) * ratio,
        };
      });
    };

    wrap.addEventListener('wheel', handleWheel, { passive: false });
    return () => wrap.removeEventListener('wheel', handleWheel);
  }, []);

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (!dragRef.current) return;
      const {
        startX, startY, originX, originY,
      } = dragRef.current;
      const dx = event.clientX - startX;
      const dy = event.clientY - startY;
      if (Math.hypot(dx, dy) >= MOSAIC_DRAG_THRESHOLD) {
        dragRef.current.moved = true;
        setDragging(true);
      }
      if (!dragRef.current.moved) return;
      setView((current) => ({
        ...current,
        x: originX + dx,
        y: originY + dy,
      }));
    };

    const handleMouseUp = () => {
      if (dragRef.current?.moved) {
        suppressClickRef.current = true;
      }
      dragRef.current = null;
      setDragging(false);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const handleMouseDown = (event) => {
    if (event.button !== 0) return;
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: view.x,
      originY: view.y,
      moved: false,
    };
  };

  const handleClick = (event) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    if (disabled || !imgRef.current || !onMosaicClick) return;
    const hit = mosaicClickToPixel(event, imgRef.current, mosaicWidth, mosaicHeight);
    if (!hit) return;
    onMosaicClick(hit.mosaicX, hit.mosaicY);
  };

  const resetView = () => setView({ x: 0, y: 0, scale: 1 });

  const placed = CORNER_ORDER.filter((label) => (
    corners?.[label]?.mosaicX != null && corners?.[label]?.mosaicY != null
  ));
  const quadPoints = placed.length === 4
    ? ['topLeft', 'topRight', 'bottomRight', 'bottomLeft']
      .map((label) => `${corners[label].mosaicX},${corners[label].mosaicY}`)
      .join(' ')
    : null;
  const activeLabel = CORNER_DISPLAY[activeCorner] || 'Top Left';
  const zoomedIn = view.scale > 1.01;

  /**
   * The overlay lives outside the CSS-scaled image so SVG and text render at
   * device resolution. Zoom is applied in SVG user space instead, which keeps
   * mosaic-pixel geometry usable as-is.
   */
  const overlayTransform = frame
    ? `translate(${view.x} ${view.y}) scale(${view.scale}) `
      + `translate(${frame.left} ${frame.top}) `
      + `scale(${frame.width / frame.srcW} ${frame.height / frame.srcH})`
    : null;

  const toScreen = (mosaicX, mosaicY) => ({
    x: view.x + (frame.left + (mosaicX / frame.srcW) * frame.width) * view.scale,
    y: view.y + (frame.top + (mosaicY / frame.srcH) * frame.height) * view.scale,
  });

  // One section name label per section (not per mapped region).
  const sectionLabels = useMemo(() => {
    const bySection = new Map();
    mappedRegions.forEach((region) => {
      const key = region.section || region.id;
      const existing = bySection.get(key);
      if (!existing) {
        bySection.set(key, {
          id: key,
          section: region.section,
          color: region.color,
          labelX: region.labelX,
          labelY: region.labelY,
          count: 1,
        });
        return;
      }
      existing.labelX += region.labelX;
      existing.labelY += region.labelY;
      existing.count += 1;
    });
    return [...bySection.values()].map((entry) => ({
      ...entry,
      labelX: entry.labelX / entry.count,
      labelY: entry.labelY / entry.count,
    }));
  }, [mappedRegions]);

  if (!safeMosaicUrl) {
    return (
      <div className={style.mapGridMosaic}>
        <div className={style.mapGridMosaicHeader}>
          <span>Mosaic URL is not allowed</span>
          <div className={style.mapGridMosaicHeaderActions}>
            <button type="button" onClick={onClose} title="Back to live view" aria-label="Close mosaic">
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={style.mapGridMosaic}>
      <div className={style.mapGridMosaicHeader}>
        <span>{`Click mosaic to set ${activeLabel} · scroll to zoom`}</span>
        <div className={style.mapGridMosaicHeaderActions}>
          {zoomedIn && (
            <button type="button" onClick={resetView} title="Reset zoom">
              Reset
            </button>
          )}
          <button type="button" onClick={onClose} title="Back to live view" aria-label="Close mosaic">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      </div>
      <div
        ref={wrapRef}
        className={`${style.mapGridMosaicClickable} ${dragging ? style.mapGridMosaicDragging : ''}`}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        onKeyDown={() => {}}
        onDoubleClick={resetView}
        role="presentation"
        title={`Click mosaic to set ${activeLabel}. Scroll to zoom, drag to pan.`}
      >
        <div
          className={style.mapGridMosaicScene}
          style={{
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
          }}
        >
          <img
            ref={imgRef}
            src={safeMosaicUrl}
            alt="Mapping mosaic"
            onLoad={updateFrame}
            draggable={false}
          />
        </div>
        {frame && (
          <div className={style.mapGridMosaicOverlay}>
            <svg className={style.mapGridQuad}>
              <g transform={overlayTransform}>
                {mappedRegions.map((region) => (
                  <g key={region.id}>
                    <polygon
                      className={style.mapGridMappedFill}
                      points={region.outline}
                      style={{ fill: region.color, stroke: region.color }}
                    />
                    {region.rowLines.map((line) => (
                      <polyline
                        key={`${region.id}-row-${line.id}`}
                        className={style.mapGridMappedLine}
                        points={line.points}
                        style={{ stroke: region.color }}
                      />
                    ))}
                    {region.seatLines.map((line) => (
                      <polyline
                        key={`${region.id}-seat-${line.id}`}
                        className={style.mapGridMappedLine}
                        points={line.points}
                        style={{ stroke: region.color }}
                      />
                    ))}
                  </g>
                ))}
                {quadPoints && (
                  <polygon className={style.mapGridDraftQuad} points={quadPoints} />
                )}
              </g>
            </svg>
            {sectionLabels.map((section) => {
              const point = toScreen(section.labelX, section.labelY);
              return (
                <span
                  key={`section-${section.id}`}
                  className={style.mapGridSectionLabel}
                  style={{ left: point.x, top: point.y, color: section.color }}
                >
                  {section.section}
                </span>
              );
            })}
            {mappedRegions.map((region) => {
              // On-screen seat spacing — hide labels until they won't collide.
              const seatScreenPitch = region.pitch * (frame.width / frame.srcW) * view.scale;
              if (seatScreenPitch < 28) return null;
              return region.seats.map((seat) => {
                const point = toScreen(seat.mosaicX, seat.mosaicY);
                return (
                  <span
                    key={`${region.id}-${seat.row}-${seat.seat}`}
                    className={style.mapGridSeatLabel}
                    style={{ left: point.x, top: point.y }}
                  >
                    {`${seat.row}-${seat.seat}`}
                  </span>
                );
              });
            })}
            {placed.map((label) => {
              const point = toScreen(corners[label].mosaicX, corners[label].mosaicY);
              return (
                <span
                  key={label}
                  className={`${style.mapGridMarker} ${
                    activeCorner === label ? style.mapGridMarkerActive : ''
                  }`}
                  style={{ left: point.x, top: point.y }}
                >
                  {CORNER_SHORT[label]}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MapGridMosaic;
